// Colores de la marca activa para el editor gráfico: normaliza `brand_profiles.colors`
// (jsonb, dos formas posibles) + hook con estado. Autónomo (no depende del árbol del
// editor de video). Unifica lo que antes duplicaba MarcaPanel.

import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

/**
 * Normaliza `brand_profiles.colors` a hex de 6 dígitos en minúscula. Acepta tanto
 * `string[]` de hex (con o sin `#`, 3 u 8 dígitos) como objetos `{hex,name,role}[]`
 * (formato de Estudio Virtual). Descarta lo inválido y deduplica.
 */
export function normalizeBrandHexColors(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const c of raw) {
    let hex =
      typeof c === 'string'
        ? c
        : c && typeof c === 'object' && 'hex' in (c as object)
          ? String((c as { hex: unknown }).hex ?? '')
          : '';
    if (typeof hex !== 'string') continue;
    hex = hex.trim();
    if (!hex) continue;
    if (!hex.startsWith('#')) hex = `#${hex}`;
    // #abc → #aabbcc
    if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
      hex = `#${hex.slice(1).split('').map((ch) => ch + ch).join('')}`;
    }
    // #rrggbbaa → #rrggbb (descarta alpha)
    if (/^#[0-9a-fA-F]{8}$/.test(hex)) hex = hex.slice(0, 7);
    if (/^#[0-9a-fA-F]{6}$/.test(hex)) out.push(hex.toLowerCase());
  }
  return [...new Set(out)];
}

/** Colores de la marca activa. `[]` si no hay marca o no tiene colores cargados. */
export function useBrandColors(brandId: string | null): string[] {
  const [colors, setColors] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!brandId) {
        setColors([]);
        return;
      }
      const { data } = await supabase
        .from('brand_profiles')
        .select('colors')
        .eq('id', brandId)
        .maybeSingle();
      if (!cancelled) {
        setColors(normalizeBrandHexColors((data as { colors?: unknown } | null)?.colors));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandId]);
  return colors;
}
